import { useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { cx } from './cx';
import { useKit } from './strings';

export type TabItem = { id: string; label: ReactNode; count?: number; disabled?: boolean; panel?: ReactNode };

/**
 * Next enabled tab for a key press (WAI-ARIA tabs pattern). Arrow keys follow reading direction, so in RTL
 * ArrowLeft moves forward. Returns -1 for keys the tab list does not handle.
 */
export function nextTabIndex(key: string, current: number, disabled: boolean[], dir: 'ltr' | 'rtl' = 'ltr'): number {
  const n = disabled.length;
  if (!n) return -1;
  const fwd = dir === 'rtl' ? 'ArrowLeft' : 'ArrowRight';
  const back = dir === 'rtl' ? 'ArrowRight' : 'ArrowLeft';
  const scan = (start: number, step: 1 | -1) => {
    for (let k = 0, i = start; k < n; k++, i = (i + step + n) % n) if (!disabled[i]) return i;
    return -1;
  };
  if (key === fwd) return scan((current + 1) % n, 1);
  if (key === back) return scan((current - 1 + n) % n, -1);
  if (key === 'Home') return scan(0, 1);
  if (key === 'End') return scan(n - 1, -1);
  return -1;
}

/**
 * Tabs with roving focus (only the selected tab is in the tab order), automatic activation, RTL-aware arrows.
 * Controlled (value + onChange) or uncontrolled (defaultValue). Panels render only for the selected tab.
 */
export function Tabs({ items, value, defaultValue, onChange, label, variant = 'pill', className }: {
  items: TabItem[]; value?: string; defaultValue?: string; onChange?: (id: string) => void; label: string; variant?: 'pill' | 'underline'; className?: string;
}) {
  const { dir } = useKit();
  const base = useId().replace(/:/g, '');
  const [inner, setInner] = useState(defaultValue ?? items.find((t) => !t.disabled)?.id ?? '');
  const selected = value ?? inner;
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const select = (id: string) => { if (value === undefined) setInner(id); onChange?.(id); };
  const onKey = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    const next = nextTabIndex(e.key, i, items.map((t) => Boolean(t.disabled)), dir);
    if (next < 0) return;
    e.preventDefault();
    refs.current[next]?.focus();
    select(items[next]!.id);
  };
  const current = items.find((t) => t.id === selected);
  return (
    <div className={cx('qk-tabs', className)} data-variant={variant}>
      <div className="qk-tabs__list" role="tablist" aria-label={label}>
        {items.map((t, i) => {
          const on = t.id === selected;
          return (
            <button
              key={t.id}
              ref={(el) => { refs.current[i] = el; }}
              type="button"
              role="tab"
              id={`${base}-tab-${t.id}`}
              aria-selected={on}
              aria-controls={t.panel !== undefined ? `${base}-panel-${t.id}` : undefined}
              tabIndex={on ? 0 : -1}
              disabled={t.disabled}
              className="qk-tab"
              onClick={() => select(t.id)}
              onKeyDown={(e) => onKey(e, i)}
            >
              {t.label}
              {t.count !== undefined ? <span className="qk-tab__count">{t.count}</span> : null}
            </button>
          );
        })}
      </div>
      {current && current.panel !== undefined ? (
        <div className="qk-tabs__panel" role="tabpanel" id={`${base}-panel-${current.id}`} aria-labelledby={`${base}-tab-${current.id}`} tabIndex={0}>
          {current.panel}
        </div>
      ) : null}
    </div>
  );
}
